import {Component, ElementRef, inject, Input, OnChanges, SimpleChanges, TemplateRef} from '@angular/core';
import {
    ConnectionType,
    CustomCorrectionOption,
    GetCustomCorrectionOptions,
    MeterTreeMeasurementsResult,
    StartCustomCorrectingPoller
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {Entity} from "../../../handlers/entity";
import {AppContext} from '../../../app-context';
import {RefdataUtils} from '../../refdata/refdata-utils';
import {openConfirmationModalWithCallback} from '../../../app-utils';
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from '../../../common/modal-confirm/modal-confirm.component';
import {AppCommonUtils, sendCommand} from '../../../common/app-common-utils';
import {QueryGateway} from '../../../common/query-gateway';
import {hasEligibleMeterTreeMeters} from '../../charts/intermediate-meter.utils';

@Component({
    selector: 'app-location-dashboard',
    templateUrl: './location-dashboard.component.html',
    styleUrls: ['./location-dashboard.component.scss']
})
export class LocationDashboardComponent implements OnChanges {
    private static readonly getCorrectionOptions =
        'com.flowmaps.api.measurements.GetCustomCorrectionOptions';
    private static readonly startCorrection =
        'com.flowmaps.api.measurements.StartCustomCorrectingPoller';

    appContext = AppContext;
    private readonly queryGateway = inject(QueryGateway);
    private readonly elementRef = inject(ElementRef);
    @Input() locationEntity: Entity | null = null;
    correctionOptions: CustomCorrectionOption[] = [];
    selectedCorrectionMeterId: string | null = null;
    correctionPending = false;
    meterTreeDataAvailable: boolean | null = null;
    meterTreeMeasurements: MeterTreeMeasurementsResult | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.locationEntity) {
            this.meterTreeDataAvailable = null;
            this.meterTreeMeasurements = null;
            this.loadCorrectionOptions();
        }
    }

    onMeterTreeDataAvailable(available: boolean): void {
        this.meterTreeDataAvailable = available;
    }

    hasConnectionType(type: ConnectionType): boolean {
        return this.locationEntity?.location?.connections?.some(c => c?.info?.connectionType === type) ?? false;
    }

    get hasElectricityConnection(): boolean {
        return this.hasConnectionType(ConnectionType.Electricity);
    }

    get hasGasOrHeatConnection(): boolean {
        return this.locationEntity?.location?.connections?.some(
            c => c?.info?.connectionType === ConnectionType.Gas || c?.info?.connectionType === ConnectionType.Heat
        ) ?? false;
    }

    get hasWaterConnection(): boolean {
        return this.hasConnectionType(ConnectionType.Water);
    }

    get hasChargePointConnection(): boolean {
        return this.hasConnectionType(ConnectionType.ChargePoint);
    }

    get hasMeterTree(): boolean {
        return hasEligibleMeterTreeMeters(this.locationEntity?.location);
    }

    get selectedCorrectionOption(): CustomCorrectionOption | undefined {
        return this.correctionOptions.find(option => option.meterId === this.selectedCorrectionMeterId);
    }

    openCustomCorrectionModal(body: TemplateRef<unknown>): void {
        this.selectedCorrectionMeterId = this.correctionOptions[0]?.meterId || null;
        if (!this.selectedCorrectionMeterId) return;

        openConfirmationModalWithCallback(confirmed => {
            if (confirmed && this.selectedCorrectionMeterId) {
                this.startCustomCorrection(this.selectedCorrectionMeterId);
            }
        }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
            type: 'info',
            title: 'Correct meter data',
            body,
            cancelText: 'Cancel',
            confirmText: 'Start correction'
        }, 'static');
    }

    correctionOptionLabel(option: CustomCorrectionOption): string {
        for (const connection of this.locationEntity?.location?.connections || []) {
            const meter = connection.meters?.find(candidate => candidate.meterId === option.meterId);
            if (meter) {
                return RefdataUtils.meterFormatter(new Entity(
                    this.locationEntity.organisation, this.locationEntity.location, connection, meter));
            }
        }
        return option.meterId;
    }

    formatCorrectionTimestamp(timestamp?: string): string {
        if (!timestamp) return '';
        return new Intl.DateTimeFormat(AppContext.getPreferredLanguage() === 'nl' ? 'nl-NL' : 'en-GB', {
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZone: String(this.locationEntity?.location?.info?.timeZone || 'Europe/Amsterdam')
        }).format(new Date(timestamp));
    }

    private loadCorrectionOptions(): void {
        this.correctionOptions = [];
        this.selectedCorrectionMeterId = null;
        const locationId = this.locationEntity?.location?.locationId;
        if (!AppContext.isAdminOrIntermediary() || !locationId) return;

        this.queryGateway.send(LocationDashboardComponent.getCorrectionOptions,
            <GetCustomCorrectionOptions>{locationId}, {caching: false}, this.elementRef).subscribe({
            next: options => this.correctionOptions = options,
            error: error => AppCommonUtils.registerError(error)
        });
    }

    private startCustomCorrection(meterId: string): void {
        if (this.correctionPending) return;
        this.correctionPending = true;
        sendCommand(LocationDashboardComponent.startCorrection,
            <StartCustomCorrectingPoller>{meterId}, () => {
                this.correctionPending = false;
                this.correctionOptions = this.correctionOptions.filter(option => option.meterId !== meterId);
                this.selectedCorrectionMeterId = null;
                AppCommonUtils.registerSuccess('Meter data correction has been started');
            }, error => {
                this.correctionPending = false;
                AppCommonUtils.registerError(error);
            }, {eventOnSuccess: true}, this.elementRef);
    }
}
